import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lightbulb } from "lucide-react";
import { api } from "../lib/api";
import { fuzzyMatch } from "../lib/match";
import { applyMedResult } from "../lib/sr";
import { hintChime } from "../lib/audio";
import { recordSession } from "../lib/sessions";
import { awardCoins } from "../lib/coins";
import { useProgress } from "../context/ProgressContext";
import { useModality } from "../context/ModalityContext";
import { StudyTag } from "../components/ModalitySwitch";
import { StudySummary } from "../components/StudySummary";

const DURATIONS = [1, 5, 10]; // minutes
const CUSTOM_MIN = 1;
const CUSTOM_MAX = 60;
const INITIAL_POINTS = 50;
const SPEEDS = [
  { id: "government", label: "Government", desc: "Slow and steady. 18 seconds per clue.", fall: 18, mult: 1 },
  { id: "human", label: "Human", desc: "The standard pace. 12 seconds per clue.", fall: 12, mult: 1 },
  { id: "supersonic", label: "Super Sonic", desc: "Blink and it's gone. 7 seconds per clue · 1.5× points on every correct answer.", fall: 7, mult: 1.5 },
];
const speedOf = (id) => SPEEDS.find((s) => s.id === id) || SPEEDS[1];
const fallSecondsFor = (speed) => speedOf(speed).fall;
const HINT_COST = 5;
const HINT_UNLOCK_AT = 0.5;
const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

export default function MedicationGame() {
  const { progress, update } = useProgress();
  const { isStudy } = useModality();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [minutes, setMinutes] = useState(5);
  const [custom, setCustom] = useState(false);
  const [mode, setMode] = useState("hardcore"); // easy | hardcore
  const [speed, setSpeed] = useState("human"); // government | human | supersonic
  const [stage, setStage] = useState("select"); // select | playing
  const [meds, setMeds] = useState([]);
  const [runStudy, setRunStudy] = useState(false); // modality frozen at run start

  useEffect(() => {
    api.get("/medications/classes").then((r) => {
      setClasses(r.data);
      setClassId((prev) => prev || (r.data[0]?.id ?? ""));
    });
  }, []);

  const startGame = async () => {
    const { data } = await api.get(`/medications`, { params: { class_id: classId } });
    setMeds(data);
    setRunStudy(isStudy);
    setStage("playing");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      {stage === "select" && (
        <SelectScreen
          classes={classes}
          classId={classId}
          setClassId={setClassId}
          minutes={minutes}
          setMinutes={setMinutes}
          custom={custom}
          setCustom={setCustom}
          mode={mode}
          setMode={setMode}
          speed={speed}
          setSpeed={setSpeed}
          study={isStudy}
          onStart={startGame}
        />
      )}
      {stage === "playing" && (
        <GameRunner
          meds={meds}
          classId={classId}
          durationSec={Math.round(minutes * 60)}
          mode={mode}
          speed={speed}
          study={runStudy}
          progress={progress}
          update={update}
          onRestart={() => setStage("select")}
        />
      )}
    </div>
  );
}

const MODES = [
  { id: "easy", label: "Easy", desc: "Multiple choice — pick the right drug from four options. No hints needed.", studyDesc: "Multiple choice — pick the right drug from four options. Miss it and the right one lights up to tap." },
  { id: "hardcore", label: "Hardcore", desc: "Type the generic or brand name before the clue hits the floor. Hints cost points.", studyDesc: "Type the generic or brand name at your own pace. Hints are free." },
];

function SelectScreen({ classes, classId, setClassId, minutes, setMinutes, custom, setCustom, mode, setMode, speed, setSpeed, study, onStart }) {
  const validMinutes = study || (Number.isFinite(minutes) && minutes >= CUSTOM_MIN && minutes <= CUSTOM_MAX);
  return (
    <>
      <div className="mb-10">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// MEDICATION DRILL{study && <StudyTag testid="med-select-study-tag" />}</div>
        <h1 className="text-4xl font-extrabold tracking-tight">Pick a class.</h1>
      </div>

      <div className="panel p-6 mb-6">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">DRUG CLASS</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {classes.map((c) => (
            <button
              key={c.id}
              onClick={() => setClassId(c.id)}
              data-testid={`class-${c.id}`}
              className={`text-left px-4 py-3 border transition-colors ${
                classId === c.id
                  ? "border-phosphor bg-primary/10 text-phosphor"
                  : "border-border hover:border-phosphor/60"
              }`}
            >
              <div className="text-sm font-semibold">{c.name}</div>
              <div className="text-[10px] tracking-widest text-muted-foreground mt-1">{c.count} MEDS</div>
            </button>
          ))}
        </div>
      </div>

      <div className="panel p-6 mb-6">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">MODE</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              data-testid={`mode-${m.id}`}
              className={`text-left px-4 py-3 border transition-colors ${
                mode === m.id
                  ? m.id === "hardcore" ? "border-destructive/70 bg-destructive/10 text-destructive" : "border-phosphor bg-primary/10 text-phosphor"
                  : "border-border hover:border-phosphor/60"
              }`}
            >
              <div className="text-sm font-semibold tracking-widest uppercase">{m.label}</div>
              <div className="text-xs text-muted-foreground mt-1 normal-case">{study ? m.studyDesc : m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {study ? (
        <div className="panel p-6 mb-8 border-sky-400/40" data-testid="med-study-note">
          <div className="text-[10px] tracking-[0.3em] text-sky-300 mb-2">STUDY MODE</div>
          <div className="text-sm text-muted-foreground">
            No clock, no falling clue, no points on screen. Miss one and the answer appears — type or tap it to lock it in, then keep going.
            Hints are free. Press <span className="text-foreground">End Session</span> whenever you're done to review what you missed. Coins still accrue quietly.
          </div>
        </div>
      ) : (
      <>
      <div className="panel p-6 mb-6">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">SPEED</div>
        <div className="grid sm:grid-cols-3 gap-2">
          {SPEEDS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSpeed(s.id)}
              data-testid={`speed-${s.id}`}
              className={`text-left px-4 py-3 border transition-colors ${
                speed === s.id
                  ? s.id === "supersonic" ? "border-amber-400/70 bg-amber-400/10 text-amber-300" : "border-phosphor bg-primary/10 text-phosphor"
                  : "border-border hover:border-phosphor/60"
              }`}
            >
              <div className="text-sm font-semibold tracking-widest uppercase">{s.label}</div>
              <div className="text-xs text-muted-foreground mt-1 normal-case">{s.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="panel p-6 mb-8">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-1">SESSION LENGTH</div>
        <div className="text-xs text-muted-foreground mb-4">Clues keep dropping until the clock runs out (or your points hit zero).</div>
        <div className="flex flex-wrap items-center gap-2">
          {DURATIONS.map((n) => (
            <button
              key={n}
              onClick={() => { setMinutes(n); setCustom(false); }}
              data-testid={`duration-${n}`}
              className={`px-5 py-2 border text-sm font-semibold tracking-widest ${
                !custom && minutes === n
                  ? "border-phosphor text-phosphor bg-primary/10"
                  : "border-border hover:border-phosphor/60"
              }`}
            >
              {n} MIN
            </button>
          ))}
          <button
            onClick={() => setCustom(true)}
            data-testid="duration-custom"
            className={`px-5 py-2 border text-sm font-semibold tracking-widest ${
              custom ? "border-phosphor text-phosphor bg-primary/10" : "border-border hover:border-phosphor/60"
            }`}
          >
            CUSTOM
          </button>
          {custom && (
            <label className="flex items-center gap-2 ml-1 text-xs text-muted-foreground" data-testid="duration-custom-wrap">
              <input
                type="number"
                min={CUSTOM_MIN}
                max={CUSTOM_MAX}
                step={1}
                value={Number.isFinite(minutes) ? minutes : ""}
                onChange={(e) => setMinutes(e.target.value === "" ? NaN : Number(e.target.value))}
                data-testid="duration-custom-input"
                autoFocus
                className="w-24 bg-transparent border border-phosphor/40 focus:border-phosphor outline-none px-3 py-2 text-sm font-mono text-foreground"
              />
              MIN ({CUSTOM_MIN}–{CUSTOM_MAX})
            </label>
          )}
        </div>
      </div>
      </>
      )}

      <button
        onClick={onStart}
        data-testid="start-med-game-btn"
        disabled={!classId || !validMinutes}
        className="bg-primary text-primary-foreground px-8 py-4 text-sm tracking-[0.25em] uppercase font-semibold hover:bg-primary/90 disabled:opacity-50"
      >
        {study ? `Begin ${mode} Study Session →` : <>Begin {validMinutes ? `${minutes}-minute` : ""} {mode} Drill →</>}
      </button>
    </>
  );
}

/**
 * Spaced repetition: each med has `weight` in progress.medication[classId].weights[id].
 * weight starts at 1. Correct → weight * 0.6 (min 0.25). Missed → weight * 1.9 (max 4).
 */
function getWeights(progress, classId, meds) {
  const w = progress?.medication?.[classId]?.weights || {};
  return meds.map((m) => ({ med: m, weight: w[m.id] ?? 1 }));
}

function weightedSample(entries, n) {
  const pool = [...entries];
  const picks = [];
  for (let i = 0; i < n && pool.length; i++) {
    const total = pool.reduce((s, e) => s + e.weight, 0);
    let r = Math.random() * total;
    let idx = 0;
    for (; idx < pool.length; idx++) {
      r -= pool[idx].weight;
      if (r <= 0) break;
    }
    picks.push(pool[Math.min(idx, pool.length - 1)].med);
    // Allow repeats across the set only if we run out
    if (pool.length > n) pool.splice(Math.min(idx, pool.length - 1), 1);
  }
  return picks;
}

function pickNext(progress, classId, meds, lastId) {
  const entries = getWeights(progress, classId, meds).filter((e) => meds.length < 2 || e.med.id !== lastId);
  return weightedSample(entries, 1)[0];
}

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

function buildOptions(current, meds) {
  const others = meds.filter((m) => m.id !== current.id).sort(() => Math.random() - 0.5).slice(0, 3);
  return [current, ...others].sort(() => Math.random() - 0.5);
}

const brandOf = (m) => m.aliases.find((a) => a !== m.generic && !m.generic.includes(a) && !a.includes(m.generic));

function GameRunner({ meds, classId, durationSec, mode, speed, study = false, progress, update, onRestart }) {
  const isEasy = mode === "easy";
  const fallSeconds = fallSecondsFor(speed);
  const multiplier = speedOf(speed).mult;
  const [points, setPoints] = useState(study ? 0 : INITIAL_POINTS);
  const [queue, setQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [guess, setGuess] = useState("");
  const [feedback, setFeedback] = useState(null); // {ok, text}
  const [missed, setMissed] = useState([]);
  const [retrying, setRetrying] = useState(false); // study: item was missed, waiting for lock-in
  const [progressStart, setProgressStart] = useState(0);
  const [stage, setStage] = useState("playing"); // playing | summary
  const [flash, setFlash] = useState(null); // 'red' | 'green'
  const [hinted, setHinted] = useState(false);
  const [hintGlow, setHintGlow] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const [timeLeft, setTimeLeft] = useState(durationSec);
  const [correctCount, setCorrectCount] = useState(0);
  const startRef = useRef(Date.now());
  const endAtRef = useRef(Date.now() + durationSec * 1000);
  const stageRef = useRef("playing");
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const inputRef = useRef(null);
  const advancingRef = useRef(false); // blocks double submits while feedback is showing
  const progressRef = useRef(progress);
  progressRef.current = progress;

  // Seed the first clue (once per drill; later progress updates must not reshuffle)
  useEffect(() => {
    setQueue([pickNext(progressRef.current, classId, meds, null)]);
    setCurrentIdx(0);
    setPoints(study ? 0 : INITIAL_POINTS);
    setProgressStart(0);
    setTimeLeft(durationSec);
    startRef.current = Date.now();
    endAtRef.current = Date.now() + durationSec * 1000;
    setTimeout(() => inputRef.current?.focus(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meds, classId, durationSec]);

  const current = queue[currentIdx];
  const options = useMemo(() => (current && isEasy ? buildOptions(current, meds) : []), [current, isEasy, meds]);
  const [picked, setPicked] = useState(null);

  // Session clock (competition only)
  useEffect(() => {
    if (stage !== "playing" || study) return;
    const tick = setInterval(() => {
      const left = Math.ceil((endAtRef.current - Date.now()) / 1000);
      setTimeLeft(Math.max(0, left));
      if (left <= 0) {
        clearInterval(tick);
        finish(pointsRef.current);
      }
    }, 250);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);


  // Falling timer (competition only — study is self-paced)
  useEffect(() => {
    if (stage !== "playing" || !current || study) return;
    startRef.current = Date.now();
    setProgressStart(0);
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const prog = Math.min(1, elapsed / fallSeconds);
      setProgressStart(prog);
      if (prog >= 1) {
        clearInterval(tick);
        onMiss(true);
      }
    }, 50);
    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, stage, queue.length]);

  const commitWeightChange = (medId, correct) => {
    update({ medication: applyMedResult(progressRef.current?.medication, classId, medId, correct) });
  };

  const finish = (nextPoints) => {
    if (stageRef.current !== "playing") return;
    stageRef.current = "summary";
    const final = Math.max(0, nextPoints);
    if (study) {
      const stats = awardCoins(progressRef.current?.stats || {}, final, "study session");
      update({ stats: { ...stats, med_study_sessions: (stats.med_study_sessions || 0) + 1, last_played: new Date().toISOString() } });
      setStage("summary");
      return;
    }
    const stats = awardCoins(progressRef.current?.stats || {}, final, "drill");
    update({
      stats: {
        ...stats,
        ...(isEasy
          ? { med_best_score_easy: Math.max(stats.med_best_score_easy || 0, final) }
          : { med_best_score: Math.max(stats.med_best_score || 0, final) }),
        med_sessions: (stats.med_sessions || 0) + 1,
        last_played: new Date().toISOString(),
      },
    });
    setPoints(final);
    setStage("summary");
    recordSession({ kind: "med", category: classId, mode, score: final });
  };

  const advance = (nextPoints) => {
    if (stageRef.current !== "playing") return;
    if (!study && (nextPoints <= 0 || endAtRef.current - Date.now() <= 0)) {
      finish(nextPoints);
      return;
    }
    setQueue((q) => [...q, pickNext(progressRef.current, classId, meds, current?.id)]);
    setCurrentIdx((i) => i + 1);
    setGuess("");
    setFeedback(null);
    setHinted(false);
    setPicked(null);
    setRetrying(false);
    advancingRef.current = false;
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  useEffect(() => {
    if (!isEasy) return;
    const onKey = (e) => {
      const n = Number(e.key);
      if (n >= 1 && n <= options.length) choose(options[n - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEasy, options, picked, retrying]);

  const choose = (opt) => {
    if (!current || stageRef.current !== "playing") return;
    if (picked && !(study && retrying)) return;
    if (study && retrying) {
      if (opt.id === current.id) onCorrect();
      return;
    }
    setPicked(opt.id);
    if (opt.id === current.id) onCorrect();
    else onMiss(false);
  };

  const hintReady = study || (progressStart >= HINT_UNLOCK_AT && points >= HINT_COST);

  const useHint = () => {
    if (hinted || !hintReady || stage !== "playing") return;
    setHinted(true);
    if (!study) {
      pointsRef.current -= HINT_COST;
      setPoints(pointsRef.current);
    }
    setHintGlow(true);
    hintChime();
    setTimeout(() => setHintGlow(false), 720);
    inputRef.current?.focus();
  };

  const onMiss = (fromDrop = false) => {
    if (!current || stage !== "playing" || advancingRef.current) return;
    if (study) {
      if (!retrying) {
        commitWeightChange(current.id, false);
        setMissed((m) => [...m, current]);
        setRetrying(true);
      }
      setFlash("red");
      setFeedback({ ok: false, text: `Not quite — it's ${current.generic.toUpperCase()}. ${isEasy ? "Tap it" : "Type it"} to lock it in.` });
      setGuess("");
      setTimeout(() => setFlash(null), 240);
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    commitWeightChange(current.id, false);
    setMissed((m) => [...m, current]);
    const nextPoints = Math.max(0, pointsRef.current - (fromDrop ? 10 : 5));
    setPoints(nextPoints);
    advancingRef.current = true;
    setFlash("red");
    setFeedback({ ok: false, text: `Missed — ${current.generic.toUpperCase()}` });
    setTimeout(() => setFlash(null), 240);
    setTimeout(() => advance(nextPoints), 1600);
  };

  const onCorrect = () => {
    if (!current || advancingRef.current) return;
    advancingRef.current = true;
    if (study) {
      if (retrying) {
        setFlash("green");
        setFeedback({ ok: true, text: `Locked in • ${current.generic}` });
      } else {
        commitWeightChange(current.id, true);
        setCorrectCount((c) => c + 1);
        pointsRef.current += Math.round(15 * multiplier); // hidden — only feeds coins
        setPoints(pointsRef.current);
        setFlash("green");
        setFeedback({ ok: true, text: `Correct • ${current.generic}` });
      }
      setTimeout(() => setFlash(null), 240);
      setTimeout(() => advance(pointsRef.current), 700);
      return;
    }
    commitWeightChange(current.id, true);
    setCorrectCount((c) => c + 1);
    const bonus = Math.round((Math.round((1 - progressStart) * 10) + 5) * multiplier);
    const nextPoints = pointsRef.current + bonus;
    setPoints(nextPoints);
    setFlash("green");
    setFeedback({ ok: true, text: `+${bonus}${multiplier > 1 ? ` (×${multiplier})` : ""} • ${current.generic}` });
    setTimeout(() => setFlash(null), 240);
    setTimeout(() => advance(nextPoints), 700);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!current || !guess.trim() || stage !== "playing") return;
    if (fuzzyMatch(guess, current.aliases)) onCorrect();
    else onMiss(false);
  };

  if (stage === "summary") {
    if (study) {
      return (
        <StudySummary
          kicker={`${classId.replace(/_/g, " ").toUpperCase()} · ${isEasy ? "EASY" : "HARDCORE"}`}
          attempted={correctCount + missed.length}
          missed={missed.map((m) => ({ id: m.id, prompt: m.clue, answer: brandOf(m) ? `${m.generic} (${brandOf(m)})` : m.generic }))}
          onRestart={onRestart}
          testid="med-study-summary"
        />
      );
    }
    return <Summary points={points} missed={missed} correct={correctCount} durationSec={durationSec} onRestart={onRestart} />;
  }

  if (!current) return null;

  const staticClue = reduced || study;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground">
          // {classId.replace(/_/g, " ").toUpperCase()}
          <span className={`ml-3 px-2 py-0.5 border text-[9px] ${isEasy ? "border-phosphor/50 text-phosphor" : "border-destructive/50 text-destructive"}`} data-testid="med-mode-tag">
            {isEasy ? "EASY" : "HARDCORE"}
          </span>
          {study ? <StudyTag testid="med-study-tag" /> : (
          <span className={`ml-2 px-2 py-0.5 border text-[9px] ${multiplier > 1 ? "border-amber-400/60 text-amber-300" : "border-border text-muted-foreground"}`} data-testid="med-speed-tag">
            {speedOf(speed).label.toUpperCase()}{multiplier > 1 && ` · ×${multiplier}`}
          </span>
          )}
        </div>
        <div className="flex items-center gap-8">
          {!study && (
          <div data-testid="med-score">
            <div className="text-[10px] tracking-widest text-muted-foreground">POINTS</div>
            <div className={`text-2xl font-bold text-phosphor ${flash === "green" ? "pulse-glow" : ""}`}>{points}</div>
          </div>
          )}
          <div data-testid="med-answered">
            <div className="text-[10px] tracking-widest text-muted-foreground">CORRECT</div>
            <div className="text-2xl font-bold">
              {correctCount}
              <span className="text-sm text-muted-foreground"> / {currentIdx}</span>
            </div>
          </div>
          {study ? (
            <>
              <div data-testid="med-missed-count">
                <div className="text-[10px] tracking-widest text-muted-foreground">TO REVIEW</div>
                <div className={`text-2xl font-bold ${missed.length ? "text-destructive" : ""}`}>{missed.length}</div>
              </div>
              <button onClick={() => finish(pointsRef.current)} data-testid="med-end-session-btn" className="border border-sky-400/60 text-sky-300 px-4 py-2 text-[10px] tracking-[0.25em] font-semibold hover:bg-sky-400/10">
                END SESSION
              </button>
            </>
          ) : (
          <div data-testid="med-clock">
            <div className="text-[10px] tracking-widest text-muted-foreground">TIME</div>
            <div className={`text-2xl font-bold tabular-nums ${timeLeft <= 10 ? "text-destructive" : ""}`}>{fmtTime(timeLeft)}</div>
          </div>
          )}
        </div>
      </div>

      <div
        className={`panel scanlines relative overflow-hidden mb-4 ${flash === "red" ? "flash-red" : ""}`}
        style={{ height: "60vh", minHeight: "420px" }}
        data-testid="med-canvas"
        data-motion={study ? "static" : reduced ? "reduced" : "falling"}
      >
        {reduced && !study && (
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-border" data-testid="med-timer-bar">
            <div
              className={`h-full ${progressStart > 0.75 ? "bg-destructive" : "bg-primary"}`}
              style={{ width: `${(1 - progressStart) * 100}%`, transition: "width 50ms linear" }}
            />
          </div>
        )}
        <div
          className={`absolute left-1/2 -translate-x-1/2 max-w-[80%] w-full text-center transition-none ${staticClue ? "top-1/2 -translate-y-1/2" : ""}`}
          style={staticClue ? undefined : { top: `${progressStart * 85}%`, transition: "top 50ms linear" }}
        >
          <div className="text-[10px] tracking-[0.4em] text-muted-foreground mb-3">
            CLUE{reduced && !study && <span className="ml-3 text-amber-300/80">· {Math.ceil((1 - progressStart) * fallSeconds)}s</span>}
          </div>
          <div className="text-lg sm:text-xl leading-snug text-foreground" data-testid="med-clue">
            {current.clue}
          </div>
          {hinted && (
            <div className={`mt-4 inline-block border border-amber-400/60 text-amber-300 px-4 py-2 text-sm tracking-[0.3em] font-bold ${hintGlow ? "hint-glow" : ""}`} data-testid="med-hint">
              {current.generic[0].toUpperCase()}
              {"_ ".repeat(current.generic.length - 1).trim()}
            </div>
          )}
        </div>

        {!isEasy && <button
          type="button"
          onClick={useHint}
          disabled={hinted || !hintReady}
          data-testid="med-hint-btn"
          title="Reveal the first letter"
          className={`absolute top-4 right-4 inline-flex items-center gap-2 px-3 py-2 border text-[10px] tracking-[0.25em] font-semibold transition-all ${
            hintGlow ? "hint-glow border-amber-400 text-amber-300" : ""
          } ${
            hinted
              ? "border-border text-muted-foreground opacity-40"
              : hintReady
                ? "border-amber-400 text-amber-300 hover:bg-amber-400/10 pulse-glow"
                : "border-border text-muted-foreground opacity-40 cursor-not-allowed"
          }`}
        >
          <Lightbulb className="w-3.5 h-3.5" /> HINT{study ? "" : ` −${HINT_COST}`}
        </button>}

        {study && retrying && !feedback?.ok && (
          <button type="button" onClick={() => advance(pointsRef.current)} data-testid="med-skip-btn" className="absolute top-4 left-4 px-3 py-2 border border-border text-[10px] tracking-[0.25em] text-muted-foreground hover:text-foreground hover:border-phosphor/60">
            SKIP →
          </button>
        )}

        {feedback && (
          <div
            data-testid="med-feedback"
            className={`absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 border text-sm font-semibold tracking-widest ${
              feedback.ok ? "border-phosphor text-phosphor" : "border-destructive text-destructive"
            }`}
          >
            {feedback.text}
          </div>
        )}
      </div>

      {isEasy ? (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="med-options">
          {options.map((opt, i) => {
            const isRight = opt.id === current.id;
            const show = picked !== null;
            const lockIn = study && retrying && isRight;
            const cls = !show
              ? "border-phosphor/30 hover:border-phosphor hover:bg-primary/10"
              : isRight
                ? `border-phosphor bg-primary/10 text-phosphor ${lockIn ? "pulse-glow cursor-pointer" : ""}`
                : picked === opt.id
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : "border-border opacity-40";
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => choose(opt)}
                disabled={show && !lockIn}
                data-testid={`med-option-${i}`}
                data-option-id={opt.id}
                className={`text-left px-4 py-3 border transition-colors flex items-center gap-3 ${cls}`}
              >
                <span className="w-6 h-6 shrink-0 flex items-center justify-center border border-current text-[10px] font-bold">{i + 1}</span>
                <span>
                  <span className="text-sm font-semibold capitalize">{opt.generic}</span>
                  {brandOf(opt) && <span className="text-xs text-muted-foreground ml-2 capitalize">({brandOf(opt)})</span>}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <form onSubmit={submit} className="flex gap-3" data-testid="med-form">
          <input
            ref={inputRef}
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder="Type the medication name..."
            data-testid="med-input"
            className="flex-1 bg-transparent border border-phosphor/30 focus:border-phosphor focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono"
            autoComplete="off"
          />
          <button
            type="submit"
            data-testid="med-submit"
            className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] font-semibold hover:bg-primary/90"
          >
            ENTER
          </button>
        </form>
      )}
    </div>
  );
}

function Summary({ points, missed, correct, durationSec, onRestart }) {
  const total = correct + missed.length;
  return (
    <div className="panel p-10 text-center" data-testid="med-summary">
      <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-3">// SESSION COMPLETE · {fmtTime(durationSec)} DRILL</div>
      <h2 className="text-5xl font-extrabold text-phosphor mb-2">{points}</h2>
      <div className="text-sm text-muted-foreground tracking-widest">POINTS</div>
      <div className="my-8 h-px bg-border" />
      <div className="text-sm text-muted-foreground" data-testid="med-summary-line">
        You answered <span className="text-foreground font-bold">{correct}</span> of {total} correctly
        {total > 0 && <> · <span className="text-foreground font-bold">{(total / Math.max(1, durationSec / 60)).toFixed(1)}</span> clues/min</>}.
      </div>
      {missed.length > 0 && (
        <div className="mt-6 text-left">
          <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-3">MISSED</div>
          <div className="space-y-2">
            {missed.map((m, i) => (
              <div key={m.id + i} className="border border-border p-3">
                <div className="text-phosphor font-semibold">{m.generic}</div>
                <div className="text-xs text-muted-foreground">{m.clue}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-8 flex gap-3 justify-center">
        <button onClick={onRestart} className="border border-phosphor/50 px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/10" data-testid="play-again-btn">
          Play Again
        </button>
        <Link to="/dashboard" className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-widest uppercase hover:bg-primary/90" data-testid="back-dashboard-btn">
          Dashboard
        </Link>
      </div>
    </div>
  );
}
